import {
  setPointLabel,
  useAppSelector,
  useAppDispatch,
  setDeliveryZone,
  clearDeliveryZone,
  setNationwideInfo,
  setDeliveryAddress,
  NationwideInfoState,
  setAnotherPointLabel,
  clearDeliveryAddress,
  setAnotherDeliveryZone,
  setAreManualCoordinates,
  clearAnotherDeliveryZone,
  setAnotherNationwideInfo,
  setAnotherDeliveryAddress,
  clearAnotherDeliveryAddress,
  clearDeliveryAddressDetails,
  setDeliveryAddressAndCoords,
  setDeliveryAddressComponents,
  setAreAnotherManualCoordinates,
  clearAnotherDeliveryAddressDetails,
  setAnotherDeliveryAddressAndCoords,
  setAnotherDeliveryAddressComponents,
} from '@/app/store';
import { ClientOnlinePaymentDto, LocationPointDto, PaymentTypeEnum } from '@/app/store/api/restApi';
import { useCallback, useMemo } from 'react';

export const useChangePersonalInfoDelivery = (isOrderAnotherPerson: boolean) => {
  const dispatch = useAppDispatch();

  const deliveryMyself = useAppSelector((state) => state.order.delivery);
  const deliveryAnother = useAppSelector((state) => state.orderAnotherPerson.delivery);

  const delivery = useMemo(() => {
    if (isOrderAnotherPerson) return deliveryAnother;
    return deliveryMyself;
  }, [deliveryMyself, deliveryAnother, isOrderAnotherPerson]);

  const onClearDeliveryZone = useCallback(() => {
    if (isOrderAnotherPerson) dispatch(clearAnotherDeliveryZone());
    else dispatch(clearDeliveryZone());
  }, [dispatch, isOrderAnotherPerson]);

  const onClearDeliveryAddress = useCallback(() => {
    if (isOrderAnotherPerson) dispatch(clearAnotherDeliveryAddress());
    else dispatch(clearDeliveryAddress());
  }, [dispatch, isOrderAnotherPerson]);

  const onClearDeliveryAddressDetails = useCallback(() => {
    if (isOrderAnotherPerson) dispatch(clearAnotherDeliveryAddressDetails());
    else dispatch(clearDeliveryAddressDetails());
  }, [dispatch, isOrderAnotherPerson]);

  const onSetDeliveryAddress = useCallback(
    ({
      city,
      house,
      street,
      address,
      coordinates,
    }: {
      city: string;
      house: string;
      street: string;
      address?: string;
      coordinates?: LocationPointDto;
    }) => {
      if (isOrderAnotherPerson) dispatch(setAnotherDeliveryAddress({ address, coordinates, city, street, house }));
      else dispatch(setDeliveryAddress({ address, coordinates, city, street, house }));
    },
    [dispatch, isOrderAnotherPerson],
  );

  const onSetDeliveryZone = useCallback(
    ({
      price,
      zoneId,
      locationId,
      paymentTypes,
      onlinePayment,
    }: {
      price?: number;
      zoneId?: string;
      locationId?: string;
      paymentTypes?: PaymentTypeEnum[];
      onlinePayment?: ClientOnlinePaymentDto;
    }) => {
      if (isOrderAnotherPerson) dispatch(setAnotherDeliveryZone({ price, zoneId, locationId, paymentTypes, onlinePayment }));
      else dispatch(setDeliveryZone({ price, zoneId, locationId, paymentTypes, onlinePayment }));
    },
    [dispatch, isOrderAnotherPerson],
  );

  const onSetNationwideInfo = useCallback(
    (info: NationwideInfoState | undefined) => {
      if (isOrderAnotherPerson) dispatch(setAnotherNationwideInfo(info));
      else dispatch(setNationwideInfo(info));
    },
    [dispatch, isOrderAnotherPerson],
  );

  const onSetPointLabel = useCallback(
    (label: string) => {
      if (isOrderAnotherPerson) dispatch(setAnotherPointLabel(label));
      else setPointLabel(label);
    },
    [dispatch, isOrderAnotherPerson],
  );

  const onSetDeliveryAddressAndCoords = useCallback(
    ({ address, coordinates }: { address?: string; coordinates?: LocationPointDto }) => {
      if (isOrderAnotherPerson) dispatch(setAnotherDeliveryAddressAndCoords({ address, coordinates }));
      else dispatch(setDeliveryAddressAndCoords({ address, coordinates }));
    },
    [dispatch, isOrderAnotherPerson],
  );

  const onSetAreManualCoordinates = useCallback(
    (areManualCoordinates: boolean) => {
      if (isOrderAnotherPerson) dispatch(setAreAnotherManualCoordinates(areManualCoordinates));
      else dispatch(setAreManualCoordinates(areManualCoordinates));
    },
    [dispatch, isOrderAnotherPerson],
  );

  const onSetDeliveryAddressComponents = useCallback(
    ({ city, street, house }: { city: string; street: string; house: string }) => {
      if (isOrderAnotherPerson) dispatch(setAnotherDeliveryAddressComponents({ city, street, house }));
      else dispatch(setDeliveryAddressComponents({ city, street, house }));
    },
    [dispatch, isOrderAnotherPerson],
  );

  return {
    delivery,
    onSetPointLabel,
    onSetDeliveryZone,
    onClearDeliveryZone,
    onSetNationwideInfo,
    onSetDeliveryAddress,
    onClearDeliveryAddress,
    onSetAreManualCoordinates,
    onClearDeliveryAddressDetails,
    onSetDeliveryAddressAndCoords,
    onSetDeliveryAddressComponents,
  };
};
